import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import css from '@/shared/Map/map.module.css'
import tagCss from '@/shared/Tags/Tag/tag.module.css'
import equipmentStatusUtils from '@/pages/Equipment/components/Common/EquipmentStatus/equipmentStatus.utils.ts'
import equipmentUtils from '@/pages/Equipment/equipment.utils.ts'
import closeIconSrc from '@/assets/icons/close.svg'
import goToIconSrc from '@/assets/icons/externalLink.svg'
import bageCss from '@/shared/Badges/BadgeCount/badgeCount.module.css'
import { TClusterColorType } from './map.types'

interface IMarkerContentParams {
  onClose: () => void
  onNavigate: () => void
}

/**
 * Возвращает HTMLDivElement, который является контентом popup'а, отображаемого при клике по маркеру
 * */
const getMarkerPopupContent = (e: IEquipment, params: IMarkerContentParams) => {
  const node = document.createElement('div')
  node.classList.add(css.popupWrapper)

  node.innerHTML = `
          <div class="${tagCss.wrapper}" data-color="${equipmentStatusUtils.getStateColor(e.state)}">
            <p class="text-caption" style="color: inherit;">${equipmentUtils.getStateText(e.state)}</p>
          </div>
          <p class="text-nm" style="margin-top: 8px;">${equipmentUtils.getEquipmentAddress(e.location)}</p>
        `

  const closeBtn = document.createElement('img')
  closeBtn.src = closeIconSrc
  closeBtn.className = css.popupCloseWrapper
  closeBtn.width = 24
  closeBtn.height = 24
  closeBtn.onclick = params.onClose

  const gotoButton = document.createElement('img')
  gotoButton.onclick = params.onNavigate
  gotoButton.src = goToIconSrc
  gotoButton.className = css.popupGoToWrapper
  gotoButton.width = 24
  gotoButton.height = 24

  node.append(closeBtn)
  node.append(gotoButton)
  return node
}

interface IPinContentParams {
  onClick: () => void
}

/**
 * Возвращает html-элемент, кружок, обозначающий оборудование на карте. Кружок красится в нужный цвет в зависимости от статуса.
 * */
const getPinContent = (e: IEquipment, params: IPinContentParams) => {
  const newPin = document.createElement('div')
  newPin.innerHTML = `<div style="width: 24px; height: 24px; border-radius: 12px; background: ${getMarkerBgOnEquipment(e)}; border: 2px solid var(--border-secondary-color); display: flex; align-items: center; justify-content: center;"><div style="width: 12px; height: 12px; border-radius: 6px; background: #fff; cursor: pointer;" /></div>`
  newPin.onclick = params.onClick

  return newPin
}

/**
 * Возвращает html-элемент, элипс, обозначающий кол-во элементов в кластере.
 * */
const getCountCircleContent = (equipment: IEquipment[]) => {
  const circle = document.createElement('div')
  circle.classList.add('circle')
  circle.innerHTML = `
        <div class="${bageCss.wrapper}" data-type="${getCountCircleType(equipment)}" style="cursor: pointer;">
            <span class="text-sm text-white">${equipment.length + ' '}</span>
        </div>
    `
  return circle
}
const getCountCircleType = (equipment: IEquipment[]) => {
  const countByType: Record<TClusterColorType, number> = {
    PENDING: 0,
    RUNNING: 0,
    STOPPED: 0,
    CONNECTION_ERROR: 0,
    EQUIPMENT_ERROR: 0,
    DISCONNECTED: 0,
    IN_MAINTENANCE: 0,
    WARNING: 0,
  }
  for (const e of equipment) {
    switch (true) {
      case e.state === 'DISCONNECTED':
        countByType[e.state] = (countByType[e.state] || 0) + 1
        break
      case e.statusFlag === 'ERROR' || e.statusFlag === 'FATAL_FAULT':
        countByType['EQUIPMENT_ERROR'] = (countByType['EQUIPMENT_ERROR'] || 0) + 1
        break
      case e.state === 'ERROR':
        countByType['CONNECTION_ERROR'] = (countByType['CONNECTION_ERROR'] || 0) + 1
        break
      default:
        if (e.statusFlag === 'NORMAL') countByType[e.state] = (countByType[e.state] || 0) + 1
        else countByType[e.statusFlag] = (countByType[e.statusFlag] || 0) + 1
    }
  }
  let res = { state: '', count: 0 }
  Object.keys(countByType).forEach((state) => {
    if (countByType[state] > res.count) res = { state, count: countByType[state] }
  })
  switch (res.state) {
    case 'STOPPED':
    case 'DISCONNECTED':
      return 'dark'
    case 'IN_MAINTENANCE':
      return 'blue'
    case 'PENDING':
      return 'processing'
    case 'RUNNING':
      return 'success'
    case 'CONNECTION_ERROR':
    case 'EQUIPMENT_ERROR':
      return 'error'
    case 'WARNING':
      return 'warning'
  }
}

const getMarkerBgOnEquipment = (e: IEquipment) => {
  if (e.state === 'DISCONNECTED') return 'var(--bg-container-dark)' // Если лифт отключен, его статутс не актуален
  switch (true) {
    case e.statusFlag === 'IN_MAINTENANCE':
      return 'var(--text-link)'
    case e.statusFlag === 'WARNING':
      return 'var(--warning)'
    case e.statusFlag === 'ERROR':
    case e.statusFlag === 'FATAL_FAULT':
    case e.state === 'ERROR':
      return 'var(--error)'
    case e.state === 'PENDING':
      return 'var(--primary)'
    case e.state === 'STOPPED':
      return 'var(--bg-container-dark)'
    case e.state === 'RUNNING':
      return 'var(--success)'
  }
}

const mapEquipmentRenderer = { getMarkerPopupContent, getPinContent, getCountCircleContent }
export default mapEquipmentRenderer
